import type { Languages } from './i18n.language-config';

/**
 * Country handling for the console, split into the three things that used to share one name.
 *
 * 1. {@link IsoCountry} — ISO 3166-1 alpha-2. What the console speaks internally, and what D&B /
 *    `master-accounts` data actually contains. `GB`, never `UK`.
 * 2. {@link LegacyCountryToken} — the non-ISO token persisted in Cowbell data (`UK`). Produced
 *    only by {@link toLegacyCountryToken}, only at the API boundary, and only until the stored
 *    data is migrated. See that function for the deletion criteria.
 * 3. Display labels — "UK", "United Kingdom". Those are copy, they live with the other UI strings,
 *    and they are deliberately not codes. Nothing here produces them.
 *
 * The language tags are BCP-47, so their region subtag already *is* the ISO country code. There is
 * no knowledge in mapping between them — which is why the old `LANGUAGE_COUNTRY_MAPPING` was
 * deleted: two of its three entries were pure derivation and the third (`en-GB` → `UK`) was wrong.
 */

export const ISO_COUNTRIES = Object.freeze({
  US: 'US',
  GB: 'GB',
  AU: 'AU',
} as const);

export type IsoCountry = (typeof ISO_COUNTRIES)[keyof typeof ISO_COUNTRIES];

/**
 * The non-ISO spellings persisted in Cowbell data. `UK` is not an ISO 3166 code; it exists because
 * account writes persist the client's `country` verbatim and the console has always sent `UK`.
 */
export type LegacyCountryToken = 'US' | 'UK' | 'AU';

/**
 * The ISO country for a platform language. Keyed by literal tag rather than importing the
 * `languages` const so this module stays free of a runtime dependency on the language config.
 */
export const ISO_COUNTRY_BY_LANGUAGE: Readonly<Record<Languages, IsoCountry>> =
  Object.freeze({
    'en-US': 'US',
    'en-GB': 'GB',
    'en-AU': 'AU',
  });

export const getIsoCountry = (language: Languages): IsoCountry =>
  ISO_COUNTRY_BY_LANGUAGE[language];

/**
 * Every inbound spelling of a country we have seen in API responses or user input, normalised to
 * ISO. Replaces the old `COUNTRY_NAME_MAP`, which normalised the other way — towards `UK` — and so
 * quietly converted correct ISO data into the legacy token.
 */
const ISO_COUNTRY_BY_ALIAS: Readonly<Record<string, IsoCountry>> =
  Object.freeze({
    US: 'US',
    USA: 'US',
    'UNITED STATES': 'US',
    'UNITED STATES OF AMERICA': 'US',
    AMERICA: 'US',
    GB: 'GB',
    GBR: 'GB',
    UK: 'GB',
    'UNITED KINGDOM': 'GB',
    'GREAT BRITAIN': 'GB',
    BRITAIN: 'GB',
    ENGLAND: 'GB',
    AU: 'AU',
    AUS: 'AU',
    AUSTRALIA: 'AU',
  });

/**
 * Normalise any country spelling to ISO, or `null` when it is not one of the three the platform
 * supports. Use this on anything arriving from an API or a user before comparing or keying on it.
 */
export const toIsoCountry = (
  country: string | null | undefined
): IsoCountry | null => {
  if (!country) {
    return null;
  }

  return ISO_COUNTRY_BY_ALIAS[country.trim().toUpperCase()] ?? null;
};

export const isUnitedStates = (country: string | null | undefined): boolean =>
  toIsoCountry(country) === ISO_COUNTRIES.US;

export const isUnitedKingdom = (country: string | null | undefined): boolean =>
  toIsoCountry(country) === ISO_COUNTRIES.GB;

export const isAustralia = (country: string | null | undefined): boolean =>
  toIsoCountry(country) === ISO_COUNTRIES.AU;

/**
 * Translate an ISO country into the token the API still expects for a `country` request param or
 * payload field.
 *
 * This is the *only* place that knows `GB` is written as `UK` on the wire, and it exists solely
 * because the stored data says `UK`: ~21k `accounts` records in EU prod, plus `policy_px` /
 * `policy_px_revision` firmographics, agencies and submissions. fir-account's NAD filter accepts
 * both spellings (PS-8284), but the rest of the endpoints have not been verified, so we keep
 * sending the legacy token everywhere rather than per-endpoint guessing.
 *
 * Delete this function, and its call sites, once the stored data is migrated to ISO and the
 * endpoints are confirmed to accept `GB`. Nothing else in the console needs to change when it goes.
 */
export const toLegacyCountryToken = (iso: IsoCountry): LegacyCountryToken =>
  iso === ISO_COUNTRIES.GB ? 'UK' : iso;

/**
 * Normalise any country spelling to the token the API expects, or `null` when it is not a country
 * the platform supports. For values that came from a form or an API response and are on their way
 * back out; for the active platform's own country use {@link getCountryApiParam}.
 */
export const toCountryApiParam = (
  country: string | null | undefined
): LegacyCountryToken | null => {
  const iso = toIsoCountry(country);

  return iso === null ? null : toLegacyCountryToken(iso);
};

/** The `country` value to send to the API for a platform language. */
export const getCountryApiParam = (language: Languages): LegacyCountryToken =>
  toLegacyCountryToken(getIsoCountry(language));

/**
 * The platform language for a country, in any spelling. Resolves through {@link toIsoCountry}, so
 * ISO `GB` and the legacy `UK` both give `en-GB`.
 *
 * `COUNTRY_MAPPING` in the language config does the same job by exact string match. Prefer this in
 * new code: it is alias- and case-insensitive, which matters because the values arriving here come
 * straight from stored `country` fields.
 */
const LANGUAGE_BY_ISO_COUNTRY: Readonly<Record<IsoCountry, Languages>> =
  Object.freeze({
    US: 'en-US',
    GB: 'en-GB',
    AU: 'en-AU',
  });

export const getLanguageForCountry = (
  country: string | null | undefined
): Languages | null => {
  const iso = toIsoCountry(country);

  return iso === null ? null : LANGUAGE_BY_ISO_COUNTRY[iso];
};

/* -------------------------------------------------------------------------- */
/* Industry taxonomy                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Which industry classification a country uses. Keyed by ISO and resolved through
 * {@link toIsoCountry}, so a record holding `GB` and a record holding the legacy `UK` both resolve
 * to SIC. Previously keyed on the legacy token only, which meant any account whose stored country
 * was already ISO `GB` silently fell through to the US default and was read as NAICS.
 */
const INDUSTRY_CODE_PATH: Readonly<Record<IsoCountry, string>> = Object.freeze({
  GB: 'sicCode',
  US: 'naicsCode',
  AU: 'sicCode',
});

const INDUSTRY_DESCRIPTION_PATH: Readonly<
  Record<IsoCountry, { readonly PRIMARY: string; readonly SECONDARY: string }>
> = Object.freeze({
  GB: { PRIMARY: 'sicPrimaryDesc', SECONDARY: 'sicSecondaryDesc' },
  US: { PRIMARY: 'primaryIndustry', SECONDARY: 'secondaryIndustry' },
  AU: { PRIMARY: 'sicPrimaryDesc', SECONDARY: 'sicSecondaryDesc' },
});

export const getIndustryCodePath = (country: string | null | undefined) =>
  INDUSTRY_CODE_PATH[toIsoCountry(country) ?? ISO_COUNTRIES.US];

export const getIndustryPrimaryDescriptionPath = (
  country: string | null | undefined
) =>
  INDUSTRY_DESCRIPTION_PATH[toIsoCountry(country) ?? ISO_COUNTRIES.US].PRIMARY;

export const getIndustrySecondaryDescriptionPath = (
  country: string | null | undefined
) =>
  INDUSTRY_DESCRIPTION_PATH[toIsoCountry(country) ?? ISO_COUNTRIES.US]
    .SECONDARY;
